<template>
    <div>
        <Loader v-if="loading" />
        <div v-else class="container-fluid mt-45">
            <div class="row">
                <div class="col d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div>
                        <h2 class="mb-0">Notifications</h2>
                    </div>

                    <div class="mt-4 mt-md-0">
                        <button :disabled="!isNotificationManager || notifications.results.length >=3" @click="showAddNotificationModal()" class="btn btn-success btn-lg btn-assign-test btn-block-mobile lg-shadow" :class="{disabled: !isNotificationManager || notifications.results.length >=3}">Add Notification</button>
                    </div>
                </div>
            </div>

            <div v-if="notifications && notifications.results && notifications.results.length" class="row mt-45">
                <div class="col">
                    <b-table 
                        ref="table"
                        id="table"
                        class="custom-table bg-white" 
                        responsive
                        no-sort-reset
                        :busy.sync="isBusy"
                        :fields="filteredNotificationsFields" 
                        :items="notifications.results"
                    >
                        <template #cell(threshold)="data">
                            <span v-if="data.item.threshold">> {{ data.item.threshold }} mmol/L</span>
                            <span v-else>N/A</span>
                        </template>
                        <template #cell(timeHours)="data">
                            <span v-if="data.item.timeHours && data.item.timeMinutes">{{ data.item.timeHours | toTime }}:{{ data.item.timeMinutes | toTime }} {{ data.item.timeHours > 11 ? "PM" : "AM" }}</span>
                            <span v-else>N/A</span>
                        </template>
                        <template #cell(isActive)="data">
                            {{ data.item.isActive ? 'Yes' : 'No' }}
                        </template>
                        <template #cell(testTrigger)="data">
                            <span v-if="data.item.testTrigger == 0">Either</span>
                            <span v-if="data.item.testTrigger == 1">FGT</span>
                            <span v-if="data.item.testTrigger == 2">2 Hour</span>
                        </template>
                        <template #cell(action)="data">
                            <button @click="showEditNotificationModal(data.item)" :disabled="!isNotificationManager" class="btn btn-primary sm-shadow text-nowrap" :class="{disabled : !isNotificationManager}">Edit</button>
                            <button @click="removeNotification(data.item.id)" :disabled="!isNotificationManager" class="ml-2 btn btn-danger sm-shadow text-nowrap" :class="{disabled : !isNotificationManager}">Remove</button>
                        </template>
                    </b-table>
                </div> 
            </div>

            <div v-else class="alert alert-info mt-4" role="alert">
                There are currently no notifications to show
            </div>

            <div class="row mt-45">
                <div class="col d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div>
                        <h2 class="mb-0">Recipients</h2>
                    </div>

                    <div class="mt-4 mt-md-0">
                        <button :disabled="!isNotificationManager" @click="showAddNotificationRecipientModal()" class="btn btn-success btn-lg btn-assign-test btn-block-mobile lg-shadow" :class="{disabled: !isNotificationManager}">Add Recipient</button>
                    </div>
                </div>
            </div>

            <div v-if="recipients && recipients.results && recipients.results.length" class="row mt-45">
                <div class="col">
                    <b-table 
                        ref="table"
                        id="table"
                        class="custom-table bg-white" 
                        responsive
                        no-sort-reset
                        :busy.sync="isBusy"
                        :fields="filteredRecipientsFields"
                        :items="recipientsFieldsComputed"
                    >
                        <template #cell(value)="data">
                            <span v-if="data.item.type == 0">{{ data.item.value }}</span>
                            <span v-if="data.item.type == 1">{{ data.item.value }}</span>
                            <span v-if="data.item.type == 2">{{ (data.item.display ? data.item.display : 'Practitioner') }}</span>
                        </template>

                        <template #cell(notifications)="data">
                            <div class="d-flex">
                                <div v-for="check in data.item.notifications" :key="check.id" class="custom-tick notifications-tick">
                                    <div v-if="isNotificationManager" class="form-check">
                                        <input @change="toggleNotificationForRecipient(check.id, data.item.id)" class="form-check-input" type="checkbox" :value="check.id" :checked="isRecipientNotified(check.id, data.item.id)">
                                        <label class="form-check-label">
                                            {{ check.name }}
                                        </label>
                                    </div>
                                    <div v-else>
                                        {{ check.name }}
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #cell(action)="data">
                            <button @click="removeRecipient(data.item.id)" :disabled="!isNotificationManager" class="btn btn-danger sm-shadow text-nowrap" :class="{disabled : !isNotificationManager}">Remove</button>
                        </template>
                    </b-table>
                </div> 
            </div>

            <div v-else class="alert alert-info mt-4" role="alert">
                There are currently no recipients to show
            </div>

            <div class="row">
                <div class="col-md-6">
                    <p class="text-xs mt-4">
                        The intended use of this system is as an alternative to in-clinic testing by enabling the oral glucose tolerance test (OGTT) to be performed at home as a screening tool for planned care pathways. It is not an emergency service or a substitute for urgent medical care. Healthcare professionals should not solely rely on notifications to intervene in patient care. While alerts are triggered by glucose levels exceeding set thresholds, clinical judgment and comprehensive assessment remain essential for decision-making. Please note that notifications may not always arrive promptly and are provided for informational purposes only. Use this system cautiously as a supplementary tool. By enabling this feature you acknowledge the aforementioned constraints and to use this feature only for information purposes. Any implementation deviating from the intended use should be risk assessed and captured in the healthcare organisation's own DCB0160 documentation.
                    </p>
                </div>           
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapGetters, mapActions} from 'vuex'
import AddNotificationModal from '@/components/modals/AddNotificationModalComponent.vue'
import AddNotificationRecipientModal from '@/components/modals/AddNotificationRecipientModalComponent.vue'
import Loader from '../../../Common/components/Loader.vue'
export default {
    components: {
        Loader
    },
    
    data() {
        return {
            isBusy: true,
            loading: true,
            notifications: {},
            recipients: {},
            AddNotificationModal,
            AddNotificationRecipientModal
        }
    },

    async mounted() {
        try {
            await this.getNotifications()
            await this.getRecipients()
        } catch {
            this.loading = false
            this.isBusy = false
            this.setNotification({
                type: 'error',
                title: 'Error',
                message: 'Sorry, something went wrong'
            })
        } finally {
            this.loading = false
            this.isBusy = false
        }
    },

    filters: {
        toTime(val) {
            if(val < 10) return '0' + val
            return val.toString()
        }
    },

    computed: {
        ...mapGetters({
            organisationId: 'UserStore/GetUserOrganisationId',
            isNotificationManager: 'UserStore/GetUserIsNotificationManager',
            shouldRefreshNotifications: 'AppStore/GetShouldRefreshNotifications'
        }),

        recipientsFieldsComputed() {
            const notifications = JSON.parse(JSON.stringify(this.notifications.results))
            const recipients = JSON.parse(JSON.stringify(this.recipients.results))

            recipients.forEach(recipient => {
                recipient.notifications = notifications.map(x => {
                    return {
                        id: x.id,
                        name: x.name
                    }
                })
            })

            return recipients
        },

        filteredRecipientsFields() {
            return [
                {
                    key: 'name',
                    label: 'Name',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'value',
                    label: 'Value',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'notifications',
                    label: 'Notifications',
                    variant: 'text-wrap w-30'
                },
                {
                    key: 'action',
                    label: '',
                    tdClass: 'text-right'
                }
            ]
        },

        filteredNotificationsFields() {
            return [
                {
                    key: 'name',
                    label: 'Name',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'threshold',
                    label: 'Threshold',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'timeHours',
                    label: 'FGT Completed After',
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'isActive',
                    label: 'Is Active?',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'testTrigger',
                    label: 'Trigger',
                    sortable: true,
                    variant: 'text-wrap w-15'
                },
                {
                    key: 'action',
                    label: '',
                    tdClass: 'text-right'
                }
            ]
        }
    },

    watch: {
        async shouldRefreshNotifications() {
            if(this) {
                this.loading = true
                this.isBusy = true 

                try {
                    await this.getNotifications()
                    await this.getRecipients()
                } catch {
                    this.setShouldRefreshNotifications(false)
                    this.loading = false
                    this.isBusy = false
                    this.setNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Sorry, something went wrong'
                    })
                } finally {
                    this.setShouldRefreshNotifications(false)
                    this.loading = false
                    this.isBusy = false
                }
            }
        }
    },

    methods: {
        ...mapActions({
            setShouldRefreshNotifications: 'AppStore/SetShouldRefreshNotifications',
            setNotification: 'NotificationStore/SetNotification'
        }),
        
        async getNotifications() {
            try {
                let response = await axios.get(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/${this.organisationId}/100/0`)
                this.notifications = response.data
            } catch {
                throw new Exception()
            }
        },

        async getRecipients() {
            try {
                let response = await axios.get(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/${this.organisationId}/recipients/100/0`)
                this.recipients = response.data
            } catch {
                throw new Exception()
            }
        },

        getNotificationName(id) {
            var found = this.notifications.results.find(x => x.id === id)
            if(found) return found.name 
            return null
        },

        showAddNotificationModal() {
            this.$modal.show(AddNotificationModal)
        },

        showEditNotificationModal(model) {
            this.$modal.show(AddNotificationModal, {model: model})
        },

        showAddNotificationRecipientModal() {
            this.$modal.show(AddNotificationRecipientModal)
        },

        isRecipientNotified(notificationId, recipientId) {
            const recipientFound = this.recipients.results.find(x => x.id == recipientId)
            if(recipientFound) {
                if(recipientFound.notificationConfigIds.includes(notificationId)) {
                    return true
                }
            }

            return false
        },

        async toggleNotificationForRecipient(notificationId, recipientId) {
            const isRecipientNotified = this.isRecipientNotified(notificationId, recipientId)
            if(isRecipientNotified) {
                const recipientFound = this.recipients.results.find(x => x.id == recipientId)
                if(recipientFound) {
                    recipientFound.notificationConfigIds = recipientFound.notificationConfigIds.filter(x => x != notificationId)

                    const data = {
                        name: recipientFound.name,
                        isActive: recipientFound.isActive,
                        domainId: recipientFound.domainId,
                        organisationId: recipientFound.organisationId,
                        type: recipientFound.type,
                        value: recipientFound.value,
                        countryCode: null,
                        notificationConfigIds: recipientFound.notificationConfigIds
                    }

                    this.isBusy = true
                    try {
                        await axios.patch(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/recipients/${recipientId}`, data)
                        this.setNotification({
                            type: 'success',
                            title: 'Success!',
                            message: 'The data was saved successfully'
                        })
                    } catch {
                        this.setNotification({
                            type: 'error',
                            title: 'Error',
                            message: 'Sorry, something went wrong. The data was not saved'
                        })
                        this.isBusy = false
                    } finally {
                        this.isBusy = false
                    }
                }

            } else {
                const recipientFound = this.recipients.results.find(x => x.id == recipientId)
                if(recipientFound) {
                    if(!recipientFound.notificationConfigIds.includes(notificationId)) {
                        recipientFound.notificationConfigIds.push(notificationId)

                        const data = {
                            name: recipientFound.name,
                            isActive: recipientFound.isActive,
                            domainId: recipientFound.domainId,
                            organisationId: recipientFound.organisationId,
                            type: recipientFound.type,
                            value: recipientFound.value,
                            countryCode: null,
                            notificationConfigIds: recipientFound.notificationConfigIds
                        }

                        this.isBusy = true
                        try {
                            await axios.patch(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/recipients/${recipientId}`, data)
                            this.setNotification({
                                type: 'success',
                                title: 'Success!',
                                message: 'The data was saved successfully'
                            })
                        } catch {
                            this.setNotification({
                                type: 'error',
                                title: 'Error',
                                message: 'Sorry, something went wrong. The data was not saved'
                            })
                            this.isBusy = false
                        } finally {
                            this.isBusy = false
                        }
                    }
                }
            }
        },

        async removeNotification(id) {
            this.isBusy = true
            try {
                await axios.delete(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/${id}`)
                this.setShouldRefreshNotifications(true)
                this.setNotification({
                    type: 'success',
                    title: 'Success!',
                    message: 'The notification was removed successfully.'
                })
            } catch {
                this.isBusy = false
                this.setNotification({
                    type: 'error',
                    title: 'Error!',
                    message: 'Sorry, something went wrong. The notification was not removed.'
                })
            } finally {
                this.isBusy = false
            }
        },

        async removeRecipient(id) {
            this.isBusy = true
            try {
                await axios.delete(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/recipients/${id}`)
                this.setShouldRefreshNotifications(true)
                this.setNotification({
                    type: 'success',
                    title: 'Success!',
                    message: 'The recipient was removed successfully.'
                })
            } catch {
                this.isBusy = false
                this.setNotification({
                    type: 'error',
                    title: 'Error!',
                    message: 'Sorry, something went wrong. The recipient was not removed.'
                })
            } finally {
                this.isBusy = false
            }
        }
    }
}
</script>
